import { AsyncPipe, DatePipe, DOCUMENT, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { MatMenuModule } from '@angular/material/menu';
import { DevstroupeFullscreenComponent } from '@devstroupe/components/fullscreen';
import { DevstroupeLoadingBarComponent } from '@devstroupe/components/loading-bar';
import {
    DevstroupeNavigationItem,
    DevstroupeNavigationService,
    DevstroupeVerticalNavigationComponent,
} from '@devstroupe/components/navigation';
import { DevstroupeMediaWatcherService } from '@devstroupe/services/media-watcher';
import { AuthService } from 'app/core/auth/auth.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { CockpitVehicleHeaderComponent } from 'app/layout/common/cockpit-vehicle-header/cockpit-vehicle-header.component';
import { CompanySelectorComponent } from 'app/layout/common/company-selector/company-selector.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { ApiService } from 'app/shared/apis/api.service';
import { TitleService } from 'app/shared/services/title.service';
import { debounceTime, filter, firstValueFrom, interval, map, startWith, Subject, switchMap, take, takeUntil } from 'rxjs';
import { UserModel } from 'app/models/user.model';
import { DevstroupeConfig, DevstroupeConfigService } from '@devstroupe/services/config';
import { MatTooltipModule } from '@angular/material/tooltip';
import { StorageKeysService } from 'app/shared/services/storage-keys.service';
import { lang } from 'moment';
import { LanguageSelectorComponent } from 'app/shared/components/language-selector/language-selector.component';
import { LanguageService, SupportedLanguage } from 'app/services/language.service';
import { AutoTranslateService } from 'app/services/auto-translate.service';
import { AutoTranslateDirective } from 'app/shared/directives/auto-translate.directive';
import { AutoTranslatePipe } from 'app/shared/pipes/translate.pipe';
import { CompanyService } from 'app/services/company.service';
import { CompanyModel } from 'app/models/company.model';
import { Observable } from 'rxjs';
import { TimezoneService } from 'app/shared/services/timezone.service';
import { DateTime } from 'luxon';
import { isRequesterCompanyKind, isRequesterPortalContext } from 'app/core/auth/requester-user';
import { HiddenOptions, requesterNavigation } from 'app/mock-api/common/navigation/data';
import { AccessApprovalBellComponent } from 'app/layout/common/access-approval-bell/access-approval-bell.component';

@Component({
    selector: 'classic-layout',
    templateUrl: './classic.component.html',
    styleUrls: ['./classic.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        DevstroupeLoadingBarComponent,
        DevstroupeVerticalNavigationComponent,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        UserComponent,
        NgIf,
        RouterOutlet,
        RouterLink,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        NgClass,
        CompanySelectorComponent,
        CockpitVehicleHeaderComponent,
        MatTooltipModule,
        LanguageSelectorComponent,
        AutoTranslatePipe,
        AutoTranslateDirective,
        NgIf,
        NgFor,
        AsyncPipe,
        AccessApprovalBellComponent,
    ],
})
export class ClassicLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    navigationData: DevstroupeNavigationItem[];
    pageTitle: string = '';
    /** Oculta o título genérico quando o seletor de veículo do cockpit ocupa o header. */
    isCockpitRoute = false;
    /** Painel Admin (/admin/*) — usa menu próprio e esconde o seletor de empresa. */
    isAdminRoute = false;
    /**
     * Páginas Admin com filtro próprio de empresa (/admin/home, /admin/pricing):
     * com empresa filtrada o relógio segue o fuso dela; com Todos/Global libera o dropdown.
     */
    isAdminFilteredClockRoute = false;
    isAllocationEngineRoute = false;
    /** Portal do solicitante — menu reduzido e empresas requester. */
    isRequesterContext = false;
    user: UserModel;
    config = {
        logo: 'assets/images/logo/prehox-logo.png',
        logoDark: 'assets/images/logo/prehox-logo-white.png',
        class: 'bg-white dark:bg-gray-900 text-primary-900 dark:text-white',
    };
    isDark: boolean = false;
    isAuto: boolean = false;
    currentScheme: 'light' | 'dark' | 'auto' = 'light';

    /**
     * Constructor
     */ constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _devstroupeMediaWatcherService: DevstroupeMediaWatcherService,
        private _devstroupeNavigationService: DevstroupeNavigationService,
        private _titleService: TitleService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _apiService: ApiService,
        private _devstroupeConfigService: DevstroupeConfigService,
        private _storageKeysService: StorageKeysService,
        private _languageService: LanguageService,
        private _autoTranslateService: AutoTranslateService,
        private _companyService: CompanyService,
        private _timezoneService: TimezoneService,
        @Inject(DOCUMENT) private readonly _document: Document,
    ) {
        this.updateFromRouteSnapshot();
        this._router.events
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((e) => e instanceof NavigationEnd),
                map(() => this.getDeepestRouteSnapshot()),
            )
            .subscribe((snapshot: any) => {
                this.showSearch = snapshot?.data?.showSearch ?? false;
                this.ignoreSearchMobile = snapshot?.data?.ignoreSearchMobile ?? false;
            });
    }
    private intervalId: any;
    searchInputControl: UntypedFormControl = new UntypedFormControl();
    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    get isRtl(): boolean {
        return this._languageService.isRtl();
    }

    version_app: string = '';
    showSearch: boolean = false;
    ignoreSearchMobile: boolean = false;
    clockText = '--:--:--';
    currentCompany$: Observable<CompanyModel>;
    timezone$: Observable<string>;
    /** Empresas para o seletor de fuso no relógio (Admin / Motor). Used by classic.component.html clock menu. */
    clockCompanies: CompanyModel[] = [];
    private _clockCompaniesLoaded = false;
    /** /admin/home com filtro "Todos" — TimezoneService.setAllowClockCompanyPicker. */
    private _allowClockCompanyPicker = false;
    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.version_app = localStorage.getItem(this._storageKeysService.versao);
        console.log(this.version_app);
        
        // Subscribe to current company
        this.currentCompany$ = this._companyService.currentCompany$;
        
        // Subscribe to current timezone (friendly label)
        this.timezone$ = this._timezoneService.friendlyTimezone$;

        this._timezoneService.allowClockCompanyPicker$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((allow) => {
                this._allowClockCompanyPicker = allow;
                if (this.showClockCompanyPicker) {
                    this.ensureClockCompaniesLoaded();
                }
                this._changeDetectorRef.markForCheck();
            });

        interval(1000)
            .pipe(startWith(0), takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                const tz = this._timezoneService.getTimezone();
                this.clockText = DateTime.now().setZone(tz).toFormat('HH:mm:ss');
                this._changeDetectorRef.markForCheck();
            });
        
        ApiService.userCurrent.pipe(takeUntil(this._unsubscribeAll)).subscribe((user) => {
            this.user = user;
            if (user) {
                this._languageService.applyPreferredLanguageFromServerUser(user);
            }
            this.syncRequesterContext();
        });

        this.currentCompany$.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => this.syncRequesterContext());
        // Subscribe to title changes
        this._titleService.title$.pipe(takeUntil(this._unsubscribeAll)).subscribe((title: string) => {
            this.pageTitle = title;
            // Manually trigger change detection to avoid ExpressionChangedAfterItHasBeenCheckedError
            this._changeDetectorRef.detectChanges();
        });

        const syncCockpitFlag = (): void => {
            const path = this._router.url.split('?')[0];
            this.isCockpitRoute = /\/cockpit\/\d+/.test(path);
            this._changeDetectorRef.markForCheck();
        };
        syncCockpitFlag();
        this._router.events.pipe(filter((e) => e instanceof NavigationEnd), takeUntil(this._unsubscribeAll)).subscribe(() => syncCockpitFlag());

        const syncAdminFlag = (): void => {
            const path = this._router.url.split('?')[0];
            const wasTimezoneContext = this.isAdminRoute || this.isAllocationEngineRoute;
            this.isAdminRoute = path.startsWith('/admin');
            this.isAdminFilteredClockRoute =
                path === '/admin/home' ||
                path.startsWith('/admin/home/') ||
                path === '/admin/pricing' ||
                path.startsWith('/admin/pricing/');
            this.isAllocationEngineRoute = path === '/allocation-engine' || path.startsWith('/allocation-engine/');
            const isTimezoneContext = this.isAdminRoute || this.isAllocationEngineRoute;
            // Dropdown: Motor; demais Admin; home/pricing só com Todos/Global.
            if (this.showClockCompanyPicker) {
                this.ensureClockCompaniesLoaded();
            } else if (wasTimezoneContext && !isTimezoneContext) {
                this._timezoneService.clearTimezoneOverride();
                this._timezoneService.setAllowClockCompanyPicker(false);
            }
            this.setNavigationData();
            this._changeDetectorRef.markForCheck();
        };
        const syncRouteFlags = (): void => {
            syncAdminFlag();
            this.syncRequesterContext();
        };
        syncRouteFlags();
        this._router.events.pipe(filter((e) => e instanceof NavigationEnd), takeUntil(this._unsubscribeAll)).subscribe(() => syncRouteFlags());
        this._devstroupeConfigService.config$.pipe(takeUntil(this._unsubscribeAll)).subscribe((config: DevstroupeConfig) => {
            // Store the config
            // this.config = config;
            this.currentScheme = (config.scheme as 'light' | 'dark' | 'auto') ?? 'light';
            this.isDark = config.scheme == 'dark' || (config.scheme == 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);
            this.isAuto = config.scheme == 'auto';
        });

        // Subscribe to navigation data
        this._navigationService.navigation$.pipe(takeUntil(this._unsubscribeAll)).subscribe((navigation: Navigation) => {
            this.navigation = navigation;
            this.setNavigationData();
        });

        this._navigationService.get().pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe();

        // Subscribe to language changes to retranslate menu titles
        this._languageService
            .getLanguage$()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                if (this.navigation) {
                    this.translateNavigationTitles();
                }
            });
        this._activatedRoute.data.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ config, ignoreSearch }) => {
            if (config) {
                this.config = config;
            }

            // if (ignoreSearch) {
            //     this.ignoreSearch = true;
            // }
        });

        this._activatedRoute.queryParams.pipe(takeUntil(this._unsubscribeAll)).subscribe((params) => {
            this.searchInputControl.setValue(this._activatedRoute.snapshot.queryParams['text'] ?? '', { emitEvent: false });
        });

        this.searchInputControl.valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                debounceTime(500),
                switchMap((query) => {
                    this._router.navigate([], {
                        relativeTo: this._activatedRoute,
                        queryParams: {
                            text: query ? query : null,
                        },
                        queryParamsHandling: 'merge',
                    });
                    return query;
                }),
            )
            .subscribe();

        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll),
            )
            .subscribe((event) => {
                if (!this._activatedRoute.snapshot.queryParams['text']) {
                    this.searchInputControl.setValue(this._activatedRoute.snapshot.queryParams['text'] ?? '', { emitEvent: false });
                }
            });
        // Subscribe to media changes
        this._devstroupeMediaWatcherService.onMediaChange$.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ matchingAliases }) => {
            // Check if the screen is small
            this.isScreenSmall = !matchingAliases.includes('md');
        });

        // Verifica se há um scheme salvo no localStorage
        const savedScheme = localStorage.getItem(this._storageKeysService.scheme);
        if (savedScheme) {
            this._devstroupeConfigService.config = { scheme: savedScheme };
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        clearInterval(this.intervalId);
        this._document.body.classList.remove('requester-portal-active');
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Relógio vira seletor:
     * - Motor de Alocação
     * - páginas Admin (exceto home/pricing com empresa filtrada)
     * - /admin/home e /admin/pricing com filtro Todos/Global
     */
    get showClockCompanyPicker(): boolean {
        if (this.isAllocationEngineRoute) {
            return true;
        }
        if (!this.isAdminRoute) {
            return false;
        }
        if (!this.isAdminFilteredClockRoute) {
            return true;
        }
        return this._allowClockCompanyPicker;
    }

    selectClockCompany(company: CompanyModel): void {
        if (!company) return;
        this._timezoneService.setTimezoneCompanyOverride(company);
        this._changeDetectorRef.markForCheck();
    }

    friendlyTimezoneFor(company: CompanyModel): string {
        return this._timezoneService.getFriendlyTimezoneLabel(company?.timezone || this._timezoneService.getTimezone());
    }

    isClockCompanyActive(company: CompanyModel): boolean {
        const tz = company?.timezone;
        return !!tz && tz === this._timezoneService.getTimezone();
    }

    private ensureClockCompaniesLoaded(): void {
        if (this._clockCompaniesLoaded) return;
        this._clockCompaniesLoaded = true;
        this._companyService.list({ limit: 'all' }).pipe(takeUntil(this._unsubscribeAll)).subscribe({
            next: (response) => {
                this.clockCompanies = (response.items || [])
                    .filter((company) => String(company.company_kind || '').toLowerCase() !== 'requester')
                    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
                this._changeDetectorRef.markForCheck();
            },
            error: () => {
                this._clockCompaniesLoaded = false;
            },
        });
    }

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._devstroupeNavigationService.getComponent<DevstroupeVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    setNavigationData(): void {
        if (!this.navigation) return;
        // Criar deep copy para garantir detecção de mudanças
        const source = this.isAdminRoute
            ? this.navigation.admin
            : this.isRequesterContext
              ? this.navigation.requester?.length
                  ? this.navigation.requester
                  : requesterNavigation
              : this.navigation.classic;
        let items = this.deepCloneNavigation(source || []);
        // Header admin não passa pelo vertical-nav (que aplica hidden); filtrar aqui.
        if (this.isAdminRoute) {
            items = this.filterVisibleNavigation(items);
        }
        this.navigationData = items;
        this.translateNavigationTitles();
    }

    /**
     * Remove itens cujo `hidden(user)` é true e collapsables sem filhos visíveis.
     */
    private filterVisibleNavigation(items: DevstroupeNavigationItem[]): DevstroupeNavigationItem[] {
        const opts: HiddenOptions = { user: this.user };
        return items
            .map((item) => {
                const cloned: DevstroupeNavigationItem = { ...item };
                if (item.children?.length) {
                    cloned.children = this.filterVisibleNavigation(item.children);
                }
                return cloned;
            })
            .filter((item) => {
                if (typeof item.hidden === 'function' && item.hidden(opts)) {
                    return false;
                }
                if (item.type === 'collapsable' && (!item.children || item.children.length === 0)) {
                    return false;
                }
                return true;
            });
    }

    private syncRequesterContext(): void {
        const company = this._companyService.getCurrentCompany();
        const path = this._router.url.split('?')[0];
        const inRequesterPortal = isRequesterPortalContext(this.user, company, path);

        if (inRequesterPortal && !isRequesterCompanyKind(company?.company_kind)) {
            this._companyService.rememberOperationalCompanyIfNeeded();
            this._companyService
                .resolveFirstRequesterCompanyForPortal(!!this.user?.is_admin)
                .pipe(take(1))
                .subscribe((requesterCompany) => {
                    if (requesterCompany) {
                        this._companyService.setCurrentCompany(requesterCompany);
                    }
                    this.applyRequesterContext();
                });
            return;
        }

        if (!inRequesterPortal && isRequesterCompanyKind(company?.company_kind)) {
            this._companyService
                .restoreOperationalCompany()
                .pipe(take(1))
                .subscribe(() => this.applyRequesterContext());
            return;
        }

        this.applyRequesterContext();
    }

    private applyRequesterContext(): void {
        const company = this._companyService.getCurrentCompany();
        this.isRequesterContext = isRequesterPortalContext(this.user, company, this._router.url);
        this._document.body.classList.toggle('requester-portal-active', this.isRequesterContext);
        this.setNavigationData();
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Cria uma cópia profunda dos itens de navegação
     */
    private deepCloneNavigation(items: any[]): any[] {
        return items.map((item) => {
            const cloned: any = { ...item };
            if (item.children && Array.isArray(item.children)) {
                cloned.children = this.deepCloneNavigation(item.children);
            }
            return cloned;
        });
    }

    /**
     * Traduz os títulos dos menus
     */
    translateNavigationTitles(): void {
        if (!this.navigationData) return;

        const currentLanguage = this._languageService.getCurrentLanguage();

        // Se o idioma é pt-br, restaurar títulos originais
        if (currentLanguage === 'pt-br') {
            this.restoreOriginalTitles();
            return;
        }

        // Traduzir títulos
        this.translateNavigationItems(this.navigationData, currentLanguage).then(() => {
            // Usar setTimeout para garantir que todas as mudanças sejam aplicadas
            setTimeout(() => {
                // Forçar atualização criando nova referência do array (deep clone)
                this.navigationData = this.deepCloneNavigation(this.navigationData);
                this._changeDetectorRef.markForCheck();
                this._changeDetectorRef.detectChanges();
            }, 0);
        });
    }

    /**
     * Traduz recursivamente os itens de navegação
     */
    private translateNavigationItems(items: any[], language: SupportedLanguage): Promise<void> {
        const translatePromises: Promise<void>[] = [];

        items.forEach((item, index) => {
            if (item.title && typeof item.title === 'string') {
                // Armazenar título original se ainda não foi armazenado
                if (!item._originalTitle) {
                    item._originalTitle = item.title;
                }

                // Traduzir título
                const translatePromise = firstValueFrom(this._autoTranslateService.translate(item._originalTitle, language)).then(
                    (translated) => {
                        // Criar novo objeto para forçar detecção de mudanças
                        items[index] = { ...item, title: translated };
                    },
                    () => {
                        // Em caso de erro, manter título original
                        items[index] = { ...item, title: item._originalTitle || item.title };
                    },
                );
                translatePromises.push(translatePromise);
            }

            // Traduzir filhos recursivamente
            if (item.children && Array.isArray(item.children)) {
                translatePromises.push(this.translateNavigationItems(item.children, language));
            }
        });

        return Promise.all(translatePromises).then(() => {});
    }

    /**
     * Restaura os títulos originais
     */
    private restoreOriginalTitles(): void {
        if (!this.navigationData) return;
        this.restoreNavigationItems(this.navigationData);
        // Forçar atualização criando nova referência do array (deep clone)
        this.navigationData = this.deepCloneNavigation(this.navigationData);
        this._changeDetectorRef.markForCheck();
        this._changeDetectorRef.detectChanges();
    }

    /**
     * Restaura recursivamente os títulos originais
     */
    private restoreNavigationItems(items: any[]): void {
        items.forEach((item) => {
            if (item._originalTitle) {
                item.title = item._originalTitle;
            }

            if (item.children && Array.isArray(item.children)) {
                this.restoreNavigationItems(item.children);
            }
        });
    }

    /** Alterna light → dark → auto → light num único botão. */
    cycleTheme(): void {
        const next: Record<'light' | 'dark' | 'auto', 'light' | 'dark' | 'auto'> = {
            light: 'dark',
            dark: 'auto',
            auto: 'light',
        };
        this.setTheme(next[this.currentScheme]);
    }

    get themeToggleIcon(): string {
        switch (this.currentScheme) {
            case 'dark':
                return 'heroicons_outline:moon';
            case 'auto':
                return 'heroicons_solid:bolt';
            default:
                return 'heroicons_outline:sun';
        }
    }

    get themeToggleTooltip(): string {
        switch (this.currentScheme) {
            case 'dark':
                return 'Escuro';
            case 'auto':
                return 'Automático';
            default:
                return 'Claro';
        }
    }

    setTheme(scheme) {
        this._devstroupeConfigService.config = { scheme };
    }

    private updateFromRouteSnapshot() {
        const snapshot = this.getDeepestRouteSnapshot();
        this.showSearch = snapshot?.data?.showSearch ?? false;
        this.ignoreSearchMobile = snapshot?.data?.ignoreSearchMobile ?? false;
    }

    private getDeepestRouteSnapshot() {
        let r = this._activatedRoute.root;
        while (r.firstChild) r = r.firstChild;
        return r.snapshot;
    }
}
